/**
 * ODR domain vocabulary.
 *
 * These are the terms from the AM/ODR proposal (CILT, AM Steps 0-7, OPL types,
 * Problem Tag lifecycle). Server and client both import from here so a status
 * string can never drift between the two.
 *
 * Values are stored in SQLite as TEXT. Adding a value is backwards compatible;
 * renaming one is a migration.
 */

// ---------------------------------------------------------------------------
// Roles & access
// ---------------------------------------------------------------------------

export const ROLES = [
  'ADMIN',
  'OPERATOR',
  'SUPERVISOR',
  'AM_COACH',
  'MAINTENANCE',
  'QUALITY_SAFETY',
  'MANAGEMENT',
  'ENGINEERING',
] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  ADMIN: 'Administrator',
  OPERATOR: 'Operator',
  SUPERVISOR: 'Supervisor',
  AM_COACH: 'AM Coach',
  MAINTENANCE: 'Maintenance',
  QUALITY_SAFETY: 'Quality & Safety',
  MANAGEMENT: 'Management',
  ENGINEERING: 'Engineering',
};

export const USER_STATUSES = ['ACTIVE', 'INACTIVE', 'LOCKED'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const SHIFTS = ['A', 'B', 'C'] as const;
export type Shift = (typeof SHIFTS)[number];

// ---------------------------------------------------------------------------
// Equipment
// ---------------------------------------------------------------------------

export const MACHINE_STATUSES = [
  'RUNNING',
  'STOPPED',
  'MAINTENANCE',
  'OFFLINE',
  'RETIRED',
] as const;
export type MachineStatus = (typeof MACHINE_STATUSES)[number];

export const CRITICALITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export type Criticality = (typeof CRITICALITIES)[number];

// ---------------------------------------------------------------------------
// CILT — the four daily care activities (proposal §3.3)
// ---------------------------------------------------------------------------

export const CILT_TYPES = ['C', 'I', 'L', 'T', 'OTHER'] as const;
export type CiltType = (typeof CILT_TYPES)[number];

export const CILT_LABELS: Record<CiltType, string> = {
  C: 'Cleaning',
  I: 'Inspection',
  L: 'Lubrication',
  T: 'Tightening',
  OTHER: 'Other',
};

// ---------------------------------------------------------------------------
// Checklist engine
// ---------------------------------------------------------------------------

export const CHECKLIST_ITEM_TYPES = [
  'PASS_FAIL',
  'NUMERIC',
  'DROPDOWN',
  'MULTI_SELECT',
  'TEXT',
  'PHOTO',
  'SIGNATURE',
  'CONFIRMATION',
  'YES_NO',
  'COUNTER',
] as const;
export type ChecklistItemType = (typeof CHECKLIST_ITEM_TYPES)[number];

export const CHECKLIST_FREQUENCIES = [
  'PER_SHIFT',
  'DAILY',
  'EVERY_X_HOURS',
  'WEEKLY',
  'MONTHLY',
  'CUSTOM',
] as const;
export type ChecklistFrequency = (typeof CHECKLIST_FREQUENCIES)[number];

export const FREQUENCY_LABELS: Record<ChecklistFrequency, string> = {
  PER_SHIFT: 'Per shift',
  DAILY: 'Daily',
  EVERY_X_HOURS: 'Every X hours',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  CUSTOM: 'Custom',
};

/** What the engine must force the operator to do when an item comes back abnormal. */
export const FAILURE_BEHAVIOURS = [
  'NONE',
  'COMMENT',
  'EVIDENCE',
  'PROBLEM_TAG',
  'BLOCK',
] as const;
export type FailureBehaviour = (typeof FAILURE_BEHAVIOURS)[number];

export const TEMPLATE_VERSION_STATUSES = [
  'DRAFT',
  'IN_REVIEW',
  'APPROVED',
  'PUBLISHED',
  'SUPERSEDED',
] as const;
export type TemplateVersionStatus = (typeof TEMPLATE_VERSION_STATUSES)[number];

/**
 * Template version lifecycle.
 *
 * Content is editable in DRAFT only. Everything from IN_REVIEW onwards is
 * frozen, and a published version is superseded rather than altered — a run
 * recorded against v1 must render v1 forever (ADR-0005).
 */
export const TEMPLATE_VERSION_TRANSITIONS: Record<
  TemplateVersionStatus,
  readonly TemplateVersionStatus[]
> = {
  DRAFT: ['IN_REVIEW'],
  IN_REVIEW: ['APPROVED', 'DRAFT'],
  APPROVED: ['PUBLISHED', 'DRAFT'],
  PUBLISHED: ['SUPERSEDED'],
  SUPERSEDED: [],
};

/** Only a DRAFT accepts edits to its sections, items or options. */
export const EDITABLE_TEMPLATE_STATUSES: readonly TemplateVersionStatus[] = ['DRAFT'];

export const CHECKLIST_RUN_STATUSES = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'PARTIALLY_COMPLETED',
  'COMPLETED',
  'SUBMITTED',
  'VERIFIED',
  'REJECTED',
  'CANCELLED',
] as const;
export type ChecklistRunStatus = (typeof CHECKLIST_RUN_STATUSES)[number];

/** Terminal states: no further answer writes are accepted. */
export const CLOSED_RUN_STATUSES: readonly ChecklistRunStatus[] = [
  'VERIFIED',
  'CANCELLED',
];

/** Per-answer outcome. Normalised across every item type. */
export const ANSWER_RESULTS = ['PASS', 'FAIL', 'NA', 'NORMAL', 'ABNORMAL'] as const;
export type AnswerResult = (typeof ANSWER_RESULTS)[number];

// ---------------------------------------------------------------------------
// Engineering standards
// ---------------------------------------------------------------------------

/**
 * A controlled acceptance limit for a measured parameter.
 *
 * why this exists as its own entity rather than as fields on a checklist item:
 * a published checklist version is immutable, so when engineering later
 * approves a tolerance there is no way to edit the item. The standard is
 * referenced by the item and resolved at run time, which means approving a
 * tolerance switches an existing published checklist from "record only" to
 * "scored" without touching a single historical record.
 */
export const ENGINEERING_STANDARD_STATUSES = [
  'DRAFT',
  'ENGINEERING_REVIEW',
  'APPROVED',
  'ACTIVE',
  'SUPERSEDED',
  'WITHDRAWN',
] as const;
export type EngineeringStandardStatus = (typeof ENGINEERING_STANDARD_STATUSES)[number];

export const ENGINEERING_STANDARD_TRANSITIONS: Record<
  EngineeringStandardStatus,
  readonly EngineeringStandardStatus[]
> = {
  DRAFT: ['ENGINEERING_REVIEW', 'WITHDRAWN'],
  ENGINEERING_REVIEW: ['APPROVED', 'DRAFT', 'WITHDRAWN'],
  APPROVED: ['ACTIVE', 'ENGINEERING_REVIEW'],
  ACTIVE: ['SUPERSEDED', 'WITHDRAWN'],
  SUPERSEDED: [],
  WITHDRAWN: [],
};

export const ENGINEERING_STANDARD_STATUS_LABELS: Record<EngineeringStandardStatus, string> = {
  DRAFT: 'Draft',
  ENGINEERING_REVIEW: 'Engineering review',
  APPROVED: 'Approved',
  ACTIVE: 'Active — scoring enabled',
  SUPERSEDED: 'Superseded',
  WITHDRAWN: 'Withdrawn',
};

/** Only an ACTIVE standard may be used to score a reading. */
export const SCORING_STANDARD_STATUSES: readonly EngineeringStandardStatus[] = ['ACTIVE'];

/** Where the acceptance limits came from. Recorded so provenance is never lost. */
export const STANDARD_SOURCES = [
  'MANUFACTURER_MANUAL',
  'ENGINEERING_CALCULATION',
  'INTERNAL_SPECIFICATION',
  'SUPPLIER_DATASHEET',
  'REGULATORY',
  'OTHER',
] as const;
export type StandardSource = (typeof STANDARD_SOURCES)[number];

export const STANDARD_SOURCE_LABELS: Record<StandardSource, string> = {
  MANUFACTURER_MANUAL: 'Manufacturer manual',
  ENGINEERING_CALCULATION: 'Engineering calculation',
  INTERNAL_SPECIFICATION: 'Internal specification',
  SUPPLIER_DATASHEET: 'Supplier datasheet',
  REGULATORY: 'Regulatory requirement',
  OTHER: 'Other (see reference)',
};

/** How a measured checklist item is currently treated. */
export const MEASUREMENT_MODES = ['SCORED', 'RECORD_ONLY', 'AWAITING_ENGINEERING'] as const;
export type MeasurementMode = (typeof MEASUREMENT_MODES)[number];

export const MEASUREMENT_MODE_LABELS: Record<MeasurementMode, string> = {
  SCORED: 'Scored against an approved standard',
  RECORD_ONLY: 'Recorded — not scored',
  AWAITING_ENGINEERING: 'Awaiting engineering tolerance',
};

// ---------------------------------------------------------------------------
// Problem Tags
// ---------------------------------------------------------------------------

export const PROBLEM_STATUSES = [
  'OPEN',
  'ACKNOWLEDGED',
  'ASSIGNED',
  'IN_PROGRESS',
  'WAITING',
  'PENDING_VERIFICATION',
  'CLOSED',
  'REJECTED',
  'CANCELLED',
] as const;
export type ProblemStatus = (typeof PROBLEM_STATUSES)[number];

export const PROBLEM_STATUS_LABELS: Record<ProblemStatus, string> = {
  OPEN: 'Open',
  ACKNOWLEDGED: 'Acknowledged',
  ASSIGNED: 'Assigned',
  IN_PROGRESS: 'In progress',
  WAITING: 'Waiting',
  PENDING_VERIFICATION: 'Pending verification',
  CLOSED: 'Closed',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

/**
 * Allowed Problem Tag transitions. Enforced server-side in the problem service —
 * the UI uses the same map to decide which buttons to render, so the two can
 * never disagree about what is possible.
 */
export const PROBLEM_TRANSITIONS: Record<ProblemStatus, readonly ProblemStatus[]> = {
  OPEN: ['ACKNOWLEDGED', 'ASSIGNED', 'REJECTED', 'CANCELLED'],
  ACKNOWLEDGED: ['ASSIGNED', 'IN_PROGRESS', 'REJECTED', 'CANCELLED'],
  ASSIGNED: ['IN_PROGRESS', 'WAITING', 'ACKNOWLEDGED', 'CANCELLED'],
  IN_PROGRESS: ['WAITING', 'PENDING_VERIFICATION', 'ASSIGNED', 'CANCELLED'],
  WAITING: ['IN_PROGRESS', 'ASSIGNED', 'CANCELLED'],
  PENDING_VERIFICATION: ['CLOSED', 'IN_PROGRESS', 'REJECTED'],
  CLOSED: [],
  REJECTED: ['OPEN'],
  CANCELLED: [],
};

export const TERMINAL_PROBLEM_STATUSES: readonly ProblemStatus[] = [
  'CLOSED',
  'CANCELLED',
];

export const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export type Severity = (typeof SEVERITIES)[number];

export const PROBLEM_ACTION_TYPES = [
  'COMMENT',
  'STATUS_CHANGE',
  'ASSIGN',
  'DIAGNOSIS',
  'CORRECTIVE',
  'PREVENTIVE',
  'EVIDENCE',
  'REQUEST_VERIFICATION',
] as const;
export type ProblemActionType = (typeof PROBLEM_ACTION_TYPES)[number];

// ---------------------------------------------------------------------------
// One-Point Lessons (proposal §3.4)
// ---------------------------------------------------------------------------

export const OPL_TYPES = ['BASIC_KNOWLEDGE', 'PROBLEM_CASE', 'IMPROVEMENT'] as const;
export type OplType = (typeof OPL_TYPES)[number];

export const OPL_TYPE_LABELS: Record<OplType, string> = {
  BASIC_KNOWLEDGE: 'Basic Knowledge',
  PROBLEM_CASE: 'Problem Case',
  IMPROVEMENT: 'Improvement',
};

export const OPL_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'IN_REVIEW',
  'APPROVED',
  'PUBLISHED',
  'ARCHIVED',
] as const;
export type OplStatus = (typeof OPL_STATUSES)[number];

export const OPL_TRANSITIONS: Record<OplStatus, readonly OplStatus[]> = {
  DRAFT: ['SUBMITTED', 'ARCHIVED'],
  SUBMITTED: ['IN_REVIEW', 'DRAFT'],
  IN_REVIEW: ['APPROVED', 'DRAFT'],
  APPROVED: ['PUBLISHED', 'IN_REVIEW'],
  PUBLISHED: ['ARCHIVED'],
  ARCHIVED: [],
};

/** Once a revision reaches these states its content is frozen; edits fork a new revision. */
export const FROZEN_OPL_STATUSES: readonly OplStatus[] = [
  'APPROVED',
  'PUBLISHED',
  'ARCHIVED',
];

export const OPL_MEDIA_ROLES = ['BEFORE', 'AFTER', 'INSTRUCTION'] as const;
export type OplMediaRole = (typeof OPL_MEDIA_ROLES)[number];

export const OPL_TRAINING_STATUSES = [
  'ASSIGNED',
  'VIEWED',
  'TRAINING_COMPLETED',
  'COMPETENT',
  'NEEDS_RETRAINING',
] as const;
export type OplTrainingStatus = (typeof OPL_TRAINING_STATUSES)[number];

// ---------------------------------------------------------------------------
// Autonomous Maintenance steps (proposal §3.2)
// ---------------------------------------------------------------------------

export interface AmStepDefinition {
  stepNo: number;
  code: string;
  name: string;
  operatorDoes: string;
  goal: string;
  /** The pilot covers Steps 0-3 only. 4-7 exist but must never be auto-completed. */
  inPilot: boolean;
}

export const AM_STEPS: readonly AmStepDefinition[] = [
  {
    stepNo: 0,
    code: 'AM0',
    name: 'Preparation',
    operatorDoes:
      'Learn the basics, understand safety, take photos of current machine condition.',
    goal: 'Get ready before starting.',
    inPilot: true,
  },
  {
    stepNo: 1,
    code: 'AM1',
    name: 'Initial Cleaning',
    operatorDoes:
      'Do a deep clean of the machine. While cleaning, look for problems (loose parts, leaks, wear).',
    goal: '"Cleaning is checking" — discover hidden issues.',
    inPilot: true,
  },
  {
    stepNo: 2,
    code: 'AM2',
    name: 'Tackle Problem Sources & Hard-to-Reach Areas',
    operatorDoes:
      'Find where dirt, oil, or dust come from and stop them at the source. Make difficult areas easier to clean and check.',
    goal: 'Reduce cleaning time and contamination.',
    inPilot: true,
  },
  {
    stepNo: 3,
    code: 'AM3',
    name: 'Create Basic Care Standards',
    operatorDoes:
      'Operators write their own simple checklists for daily cleaning, inspection, lubrication, and tightening.',
    goal: 'Make the right way the easy way.',
    inPilot: true,
  },
  {
    stepNo: 4,
    code: 'AM4',
    name: 'General Inspection Skills',
    operatorDoes:
      'Operators learn how key parts work (bearings, belts, sensors, pneumatics) so they can inspect deeper.',
    goal: 'Detect problems earlier.',
    inPilot: false,
  },
  {
    stepNo: 5,
    code: 'AM5',
    name: 'Independent Inspection',
    operatorDoes: 'Operators perform full routine inspections on their own with confidence.',
    goal: 'Operator becomes the first line of defence.',
    inPilot: false,
  },
  {
    stepNo: 6,
    code: 'AM6',
    name: 'Standardize & Organize the Workplace',
    operatorDoes:
      'Apply 5S and visual controls so anyone can see if something is wrong.',
    goal: 'Consistent, tidy, controlled workplace.',
    inPilot: false,
  },
  {
    stepNo: 7,
    code: 'AM7',
    name: 'Full Autonomous Management',
    operatorDoes: 'Operators continuously improve their machines and coach others.',
    goal: 'Self-managing, high-performing teams.',
    inPilot: false,
  },
];

export const TRAINING_STATUSES = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'COMPLETED',
  'CERTIFIED',
] as const;
export type TrainingStatus = (typeof TRAINING_STATUSES)[number];

export const COMPETENCY_LEVELS = [
  'NONE',
  'AWARE',
  'ASSISTED',
  'INDEPENDENT',
  'CAN_TEACH',
] as const;
export type CompetencyLevel = (typeof COMPETENCY_LEVELS)[number];

export const COMPETENCY_LABELS: Record<CompetencyLevel, string> = {
  NONE: 'Not started',
  AWARE: 'Aware (knows about it)',
  ASSISTED: 'Can do with help',
  INDEPENDENT: 'Can do independently',
  CAN_TEACH: 'Can teach others',
};

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

export const SYNC_STATUSES = ['SYNCED', 'PENDING', 'ERROR'] as const;
export type SyncStatus = (typeof SYNC_STATUSES)[number];

export const SYNC_RESULTS = ['APPLIED', 'DUPLICATE', 'CONFLICT', 'ERROR'] as const;
export type SyncResult = (typeof SYNC_RESULTS)[number];

// ---------------------------------------------------------------------------
// KPI provenance (proposal §10)
// ---------------------------------------------------------------------------

/**
 * why: the proposal lists OEE and stoppage KPIs whose baselines are "to be
 * measured in Week 1" — ODR has no OEE feed. Every KPI the dashboard renders
 * carries its provenance so nobody mistakes an unavailable number for a real
 * factory measurement.
 */
export const KPI_SOURCES = [
  'ACTUAL',
  'CALCULATED',
  'IMPORTED',
  'MANUAL',
  'UNAVAILABLE',
] as const;
export type KpiSource = (typeof KPI_SOURCES)[number];

export const KPI_SOURCE_LABELS: Record<KpiSource, string> = {
  ACTUAL: 'Measured in ODR',
  CALCULATED: 'Calculated from ODR records',
  IMPORTED: 'Imported from external system',
  MANUAL: 'Manually entered',
  UNAVAILABLE: 'No data source connected',
};

// ---------------------------------------------------------------------------
// Notifications & audit
// ---------------------------------------------------------------------------

export const NOTIFICATION_TYPES = [
  'CHECKLIST_DUE',
  'CHECKLIST_OVERDUE',
  'CHECKLIST_REJECTED',
  'CHECKLIST_PENDING_VERIFICATION',
  'PROBLEM_ASSIGNED',
  'PROBLEM_CRITICAL',
  'PROBLEM_PENDING_VERIFICATION',
  'PROBLEM_CLOSED',
  'OPL_PENDING_APPROVAL',
  'OPL_PUBLISHED',
  'TRAINING_ASSIGNED',
  'TRAINING_OVERDUE',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const AUDIT_ACTIONS = [
  'LOGIN',
  'LOGIN_FAILED',
  'LOGOUT',
  'CREATE',
  'UPDATE',
  'DELETE',
  'STATUS_CHANGE',
  'SUBMIT',
  'VERIFY',
  'REJECT',
  'APPROVE',
  'PUBLISH',
  'ASSIGN',
  'CLOSE',
  'CORRECT',
  'SYNC',
  'BACKUP',
  'RESTORE',
  'CONFIG_CHANGE',
  'PERMISSION_CHANGE',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

// ---------------------------------------------------------------------------
// Analytics (Phase 12)
// ---------------------------------------------------------------------------

/**
 * What actually happened to one scheduled CILT window.
 *
 * why this sits alongside `ComplianceRow.status` rather than replacing it: the
 * operator's "what do I do now" view asks a present-tense question (is this due,
 * is it overdue right now), while analytics asks a past-tense one (was it done,
 * and was it done on time). Collapsing the two produced a status that meant
 * different things depending on who was reading it.
 */
export const CILT_OUTCOMES = [
  /** A run started inside the window and reached SUBMITTED or VERIFIED. */
  'COMPLETED',
  /** A run was done, but only after the window had closed. */
  'LATE',
  /** A run exists but not every required item was answered. */
  'PARTIAL',
  /** The window closed with no run against it. */
  'MISSED',
  /** The window has not closed yet — not counted for or against compliance. */
  'PENDING',
] as const;
export type CiltOutcome = (typeof CILT_OUTCOMES)[number];

export const CILT_OUTCOME_LABELS: Record<CiltOutcome, string> = {
  COMPLETED: 'Completed',
  LATE: 'Late',
  PARTIAL: 'Partially completed',
  MISSED: 'Missed',
  PENDING: 'Not yet due',
};

/**
 * Outcomes that count in the compliance denominator.
 *
 * PENDING is excluded: a window that has not closed cannot be a failure, and
 * counting it as one would make compliance drop through every working day and
 * recover at midnight.
 */
export const CILT_ACCOUNTABLE_OUTCOMES: readonly CiltOutcome[] = [
  'COMPLETED',
  'LATE',
  'PARTIAL',
  'MISSED',
];

/**
 * The provenance of a figure ODR is showing.
 *
 * Distinct from `KpiSource`, which describes where a KPI comes from. This
 * describes how much of what is on screen was actually captured versus
 * reconstructed — the distinction directive §27 forbids collapsing.
 */
export const DATA_QUALITY_STATES = [
  /** Captured by ODR at the moment it happened. */
  'LIVE',
  /** Reconstructed after the fact from current configuration. */
  'BACKFILL',
  /** ODR has the field but no value was recorded. */
  'UNKNOWN',
  /** No feed exists for this at all. */
  'NOT_CONNECTED',
  /** Deliberately outside scope — e.g. AM Steps 4-7 in the pilot. */
  'NOT_APPLICABLE',
] as const;
export type DataQualityState = (typeof DATA_QUALITY_STATES)[number];

export const DATA_QUALITY_LABELS: Record<DataQualityState, string> = {
  LIVE: 'Captured live',
  BACKFILL: 'Reconstructed',
  UNKNOWN: 'Not recorded',
  NOT_CONNECTED: 'No data source connected',
  NOT_APPLICABLE: 'Not applicable',
};

// ---------------------------------------------------------------------------
// Management KPIs (Phase 12D)
// ---------------------------------------------------------------------------

/**
 * The verdict on a target comparison.
 *
 * why five states and not a boolean: "not achieved" and "cannot be judged" are
 * different facts, and a management screen that renders both as a red cross is
 * the exact failure directive §29 exists to prevent. ACHIEVED is reserved for a
 * comparison that is actually valid.
 */
export const KPI_STATUSES = [
  'ACHIEVED',
  'BELOW_TARGET',
  'WITHIN_TARGET',
  /** No feed exists for this measure at all. */
  'UNAVAILABLE',
  /** A feed exists but has not produced enough to judge the target. */
  'INSUFFICIENT_DATA',
  /** Deliberately out of scope — e.g. AM Steps 4-7 during the pilot. */
  'NOT_APPLICABLE',
] as const;
export type KpiStatus = (typeof KPI_STATUSES)[number];

export const KPI_STATUS_LABELS: Record<KpiStatus, string> = {
  ACHIEVED: 'Achieved',
  BELOW_TARGET: 'Below target',
  WITHIN_TARGET: 'Within target',
  UNAVAILABLE: 'No data source connected',
  INSUFFICIENT_DATA: 'Insufficient data',
  NOT_APPLICABLE: 'Not applicable',
};

/**
 * The pilot targets, exactly as the proposal states them.
 *
 * Held in one place so they cannot drift, and so a reader can check them
 * against the source document. Three of the seven need a Week-1 baseline ODR
 * does not hold and no feed to measure against — `requiresBaseline` marks those
 * rather than letting them quietly report zero (directive §18).
 */
export interface PilotTarget {
  key: string;
  label: string;
  /** The target as written in the proposal. */
  statement: string;
  unit: string | null;
  min: number | null;
  max: number | null;
  /** Higher is better; false for targets like "-30% stoppages". */
  higherIsBetter: boolean;
  /** True when the target is a change against a baseline ODR has never held. */
  requiresBaseline: boolean;
  /** Where the number would have to come from. */
  sourceName: string;
}

export const PILOT_TARGETS: readonly PilotTarget[] = [
  {
    key: 'oee',
    label: 'OEE',
    statement: '+5 to +8 points',
    unit: 'points',
    min: 5,
    max: 8,
    higherIsBetter: true,
    requiresBaseline: true,
    sourceName: 'OEE system (not connected)',
  },
  {
    key: 'small_stoppages',
    label: 'Small stoppages',
    statement: '−30%',
    unit: '%',
    min: null,
    max: -30,
    higherIsBetter: false,
    requiresBaseline: true,
    sourceName: 'Stoppage counter (not connected)',
  },
  {
    key: 'time_between_breakdowns',
    label: 'Time between breakdowns',
    statement: '+20%',
    unit: '%',
    min: 20,
    max: null,
    higherIsBetter: true,
    requiresBaseline: true,
    sourceName: 'Breakdown log (not connected)',
  },
  {
    key: 'cilt_compliance',
    label: 'CILT compliance',
    statement: '≥95%',
    unit: '%',
    min: 95,
    max: null,
    higherIsBetter: true,
    requiresBaseline: false,
    sourceName: 'ODR checklist schedules and runs',
  },
  {
    key: 'problems_closed',
    label: 'Problem Tags closed',
    statement: '≥50',
    unit: null,
    min: 50,
    max: null,
    higherIsBetter: true,
    requiresBaseline: false,
    sourceName: 'ODR Problem Tags',
  },
  {
    key: 'operators_certified',
    label: 'Operators certified (AM Steps 0–3)',
    statement: '2 operators',
    unit: null,
    min: 2,
    max: null,
    higherIsBetter: true,
    requiresBaseline: false,
    sourceName: 'ODR training records',
  },
  {
    key: 'opls_created',
    label: 'One-Point Lessons',
    statement: '10–15',
    unit: null,
    min: 10,
    max: 15,
    higherIsBetter: true,
    requiresBaseline: false,
    sourceName: 'ODR One-Point Lessons',
  },
];

export function pilotTarget(key: string): PilotTarget | undefined {
  return PILOT_TARGETS.find((t) => t.key === key);
}
